/**
 * GisPanel — Sprint 4.2C.
 *
 * Painel à direita do mapa (substitui LayersPanel da 4.2B com versão
 * expandida que inclui:
 *   - Camadas: Devices, Regiões, POP, OLT, CTO, Splitter, Topology, Heatmap
 *   - Listas: Regiões / Nodes com ações (foco / editar / deletar)
 *   - Preferências em localStorage
 *
 * Não inclui CRUD inline — para criar/editar abre modais separados.
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "ipwd-gis-panel-prefs";

const MARKER_CATEGORIES = [
  { id: "online",    label: "Online OK",  color: "#22c55e" },
  { id: "offline",   label: "Offline",    color: "#FF5063" },
  { id: "relay_off", label: "Relé OFF",   color: "#FF8A3C" },
  { id: "weak",      label: "RSSI fraco", color: "#FFB224" },
  { id: "ota",       label: "OTA",        color: "#60a5fa" },
  { id: "action",    label: "Ação",       color: "#FFB224" },
];

const NODE_CATEGORIES = [
  { id: "POP",      label: "POP",      color: "#a78bfa", icon: "📡" },
  { id: "OLT",      label: "OLT",      color: "#60a5fa", icon: "🗄️" },
  { id: "CTO",      label: "CTO",      color: "#2BD9F0", icon: "📦" },
  { id: "SPLITTER", label: "Splitter", color: "#fbbf24", icon: "🔀" },
  { id: "OTHER",    label: "Other",    color: "#94a3b8", icon: "⬢" },
];

const HEATMAP_MODES = [
  { id: "none",      label: "Nenhum",      icon: "○" },
  { id: "rssi",      label: "RSSI",        icon: "📶" },
  { id: "offline",   label: "Offline",     icon: "🔴" },
  { id: "events",    label: "Eventos",     icon: "⚡" },
  { id: "relay_off", label: "Relay OFF",   icon: "🟠" },
];

export const DEFAULT_GIS_PREFS = {
  markersFilter: {
    online: true, offline: true, relay_off: true,
    weak: true, ota: true, action: true,
  },
  showRegions: true,
  nodeVisibility: { POP: true, OLT: true, CTO: true, SPLITTER: true, OTHER: true },
  showTopology: true,
  showCustomers: true,    // Sprint 4.2D — Clientes IXC
  heatmapMode: "none",
  collapsed: true,   // M1.1 — GIS inicia recolhido (mais área útil de mapa)
};

export function loadGisPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_GIS_PREFS;
    const parsed = JSON.parse(raw);
    // Merge com default (campos novos em sprints futuras)
    return {
      ...DEFAULT_GIS_PREFS,
      ...parsed,
      markersFilter: { ...DEFAULT_GIS_PREFS.markersFilter, ...(parsed.markersFilter || {}) },
      nodeVisibility: { ...DEFAULT_GIS_PREFS.nodeVisibility, ...(parsed.nodeVisibility || {}) },
    };
  } catch {
    return DEFAULT_GIS_PREFS;
  }
}

export function saveGisPrefs(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // quota cheio — silently
  }
}

function Section({ title, children, actions }) {
  return (
    <div className="ipwd-layers-section">
      <div className="ipwd-layers-section-head">
        <span>{title}</span>
        {actions}
      </div>
      {children}
    </div>
  );
}

function Toggle({ on, onClick, label, color, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`ipwd-layers-toggle ${on ? "is-on" : "is-off"}`}
      style={{ "--toggle-color": color }}
    >
      <span className="ipwd-layers-checkbox">{on ? "✓" : ""}</span>
      {icon ? (
        <span className="ipwd-layers-node-icon">{icon}</span>
      ) : (
        <span className="ipwd-layers-dot" />
      )}
      <span className="ipwd-layers-toggle-label">{label}</span>
    </button>
  );
}

export default function GisPanel({
  prefs, setPrefs,
  regions = [],
  nodes = [],
  customersCount = 0,        // Sprint 4.2D
  onFocusRegion,
  onEditRegion,
  onDeleteRegion,
  onCreateRegion,
  onFocusNode,
  onEditNode,
  onDeleteNode,
  onCreateNode,
  onOpenIntegrations,
  // P0-5 — controle externo de colapso (mobile coordenado). undefined no
  // desktop ⇒ usa prefs.collapsed (localStorage), comportamento atual.
  collapsedOverride,
  onToggleCollapsed,
}) {
  const setMarkersFilter = (mf) => setPrefs({ ...prefs, markersFilter: mf });
  const setShowRegions = (v) => setPrefs({ ...prefs, showRegions: v });
  const setNodeVisibility = (nv) => setPrefs({ ...prefs, nodeVisibility: nv });
  const setShowTopology = (v) => setPrefs({ ...prefs, showTopology: v });
  const setShowCustomers = (v) => setPrefs({ ...prefs, showCustomers: v });
  const setHeatmapMode = (m) => setPrefs({ ...prefs, heatmapMode: m });

  const isCtrl = collapsedOverride !== undefined;
  const collapsed = isCtrl ? collapsedOverride : prefs.collapsed;
  const setCollapsed = (v) =>
    isCtrl ? onToggleCollapsed?.(v) : setPrefs({ ...prefs, collapsed: v });

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="ipwd-layers-collapsed"
        title="Expandir painel GIS"
      >
        🗺 GIS
      </button>
    );
  }

  return (
    <div className="ipwd-gis-panel">
      <div className="ipwd-layers-header">
        <span>GIS · Camadas</span>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="ipwd-layers-collapse-btn"
          title="Recolher"
        >−</button>
      </div>

      {/* Markers */}
      <Section title="Markers">
        {MARKER_CATEGORIES.map((c) => (
          <Toggle
            key={c.id}
            on={!!prefs.markersFilter[c.id]}
            onClick={() => setMarkersFilter({ ...prefs.markersFilter, [c.id]: !prefs.markersFilter[c.id] })}
            label={c.label}
            color={c.color}
          />
        ))}
      </Section>

      {/* Regiões */}
      <Section
        title={`Regiões (${regions.length})`}
        actions={
          <button
            type="button"
            onClick={onCreateRegion}
            className="ipwd-layers-toggle-all"
            title="Desenhar nova região"
          >+ nova</button>
        }
      >
        <Toggle
          on={prefs.showRegions}
          onClick={() => setShowRegions(!prefs.showRegions)}
          label="Mostrar polígonos"
          color="#2BD9F0"
        />
        {regions.length > 0 && (
          <div className="ipwd-gis-list">
            {regions.slice(0, 20).map((r) => (
              <div key={r.id} className="ipwd-gis-list-item">
                <span
                  className="ipwd-gis-list-color"
                  style={{ background: r.color || "#2BD9F0" }}
                />
                <button
                  type="button"
                  onClick={() => onFocusRegion?.(r)}
                  className="ipwd-gis-list-name"
                  title={`Tipo: ${r.type}`}
                >{r.name}</button>
                <button
                  type="button"
                  onClick={() => onEditRegion?.(r)}
                  className="ipwd-gis-list-action"
                  title="Editar"
                >✎</button>
                <button
                  type="button"
                  onClick={() => onDeleteRegion?.(r)}
                  className="ipwd-gis-list-action is-danger"
                  title="Excluir"
                >×</button>
              </div>
            ))}
            {regions.length > 20 && (
              <div className="ipwd-gis-list-more">+{regions.length - 20} mais…</div>
            )}
          </div>
        )}
      </Section>

      {/* Infra ISP (Nodes) */}
      <Section
        title={`Infra ISP (${nodes.length})`}
        actions={
          <button
            type="button"
            onClick={onCreateNode}
            className="ipwd-layers-toggle-all"
            title="Adicionar POP/OLT/CTO"
          >+ novo</button>
        }
      >
        {NODE_CATEGORIES.map((c) => {
          const count = nodes.filter((n) => n.type === c.id).length;
          return (
            <Toggle
              key={c.id}
              on={!!prefs.nodeVisibility[c.id]}
              onClick={() => setNodeVisibility({ ...prefs.nodeVisibility, [c.id]: !prefs.nodeVisibility[c.id] })}
              label={`${c.label} (${count})`}
              color={c.color}
              icon={c.icon}
            />
          );
        })}
        {nodes.length > 0 && (
          <div className="ipwd-gis-list">
            {nodes.slice(0, 15).map((n) => {
              const cat = NODE_CATEGORIES.find((x) => x.id === n.type);
              return (
                <div key={n.id} className="ipwd-gis-list-item">
                  <span className="ipwd-gis-list-icon">{cat?.icon || "⬢"}</span>
                  <button
                    type="button"
                    onClick={() => onFocusNode?.(n)}
                    className="ipwd-gis-list-name"
                    title={`${n.type}${n.code ? ` · ${n.code}` : ""}`}
                  >{n.name}</button>
                  <button
                    type="button"
                    onClick={() => onEditNode?.(n)}
                    className="ipwd-gis-list-action"
                    title="Editar"
                  >✎</button>
                  <button
                    type="button"
                    onClick={() => onDeleteNode?.(n)}
                    className="ipwd-gis-list-action is-danger"
                    title="Excluir"
                  >×</button>
                </div>
              );
            })}
            {nodes.length > 15 && (
              <div className="ipwd-gis-list-more">+{nodes.length - 15} mais…</div>
            )}
          </div>
        )}
      </Section>

      {/* Topologia */}
      <Section title="Topologia">
        <Toggle
          on={prefs.showTopology}
          onClick={() => setShowTopology(!prefs.showTopology)}
          label="Linhas device → node"
          color="#2BD9F0"
        />
      </Section>

      {/* Clientes IXC — Sprint 4.2D */}
      <Section title={`Clientes IXC (${customersCount})`}>
        <Toggle
          on={prefs.showCustomers}
          onClick={() => setShowCustomers(!prefs.showCustomers)}
          label="🏠 Mostrar clientes IXC"
          color="#a78bfa"
        />
      </Section>

      {/* Heatmap */}
      <Section title="Heatmap">
        {HEATMAP_MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setHeatmapMode(m.id)}
            className={`ipwd-layers-heat-item ${prefs.heatmapMode === m.id ? "is-active" : ""}`}
          >
            <span className="ipwd-layers-heat-radio">
              {prefs.heatmapMode === m.id ? "●" : "○"}
            </span>
            <span className="ipwd-layers-heat-icon">{m.icon}</span>
            <span className="ipwd-layers-heat-label">{m.label}</span>
          </button>
        ))}
      </Section>

      {/* Integrações */}
      <Section title="Integrações">
        <button
          type="button"
          onClick={onOpenIntegrations}
          className="ipwd-gis-integrations-btn"
        >
          🔌 Integrações (IXC…)
        </button>
      </Section>
    </div>
  );
}
